/**
 * Body scroll lock that works on iOS Safari.
 *
 * Multiple drawers in the theme (cart, search, menu, filter, material…)
 * each toggle their own class on <body> to disable page scroll. On iOS,
 * `overflow: hidden` alone does not stop touchmove from scrolling the page
 * — the only reliable cross-platform technique is `position: fixed` with
 * the current scroll offset restored on close.
 *
 * This script watches <body> for the class set below and applies the
 * fixed-position trick when any one is present, then restores the scroll
 * position when they all clear.
 */

// Classes that always lock scroll while present on <body>.
const UNCONDITIONAL_LOCK_CLASSES = ['overflow-hidden', 'search-drawer-open', 'filter-is-open'];

// Classes whose lock is only active under a specific media query (matches the
// rules in assets/base.css for .overflow-hidden-mobile / .overflow-hidden-tablet).
const BREAKPOINT_LOCK_CLASSES = {
  'overflow-hidden-mobile': '(max-width: 749px)',
  'overflow-hidden-tablet': '(max-width: 989px)',
};

let lockedScrollY = null;

function shouldBeLocked() {
  const classes = document.body.classList;
  for (const cls of UNCONDITIONAL_LOCK_CLASSES) {
    if (classes.contains(cls)) return true;
  }
  for (const cls in BREAKPOINT_LOCK_CLASSES) {
    if (classes.contains(cls) && window.matchMedia(BREAKPOINT_LOCK_CLASSES[cls]).matches) return true;
  }
  return false;
}

function lockScroll() {
  if (lockedScrollY !== null) return;
  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  const body = document.body;
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
}

function unlockScroll() {
  if (lockedScrollY === null) return;
  const y = lockedScrollY;
  lockedScrollY = null;
  const body = document.body;
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.width = '';
  // Restore the prior position without smooth-scroll interfering.
  window.scrollTo(0, y);
}

function sync() {
  if (shouldBeLocked()) {
    lockScroll();
  } else {
    unlockScroll();
  }
}

const observer = new MutationObserver(sync);
observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

// Resize can flip a breakpoint-conditional lock on/off without any class change.
window.addEventListener('resize', sync);
